import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import {
  ArrowLeft, Phone, Mail, Building2, MapPin, Flame, Reply, Eye,
  TrendingUp, Sparkles, MessageSquare, Loader2, ExternalLink,
  Calendar, DollarSign, Bed, Send, Mic,
} from "lucide-react";
import CallButton from "@/components/CallButton";
import CallRecordingsList from "@/components/CallRecordingsList";
import BuyerBuyBoxesPanel from "@/components/BuyerBuyBoxesPanel";
import BuyerNotificationPrefCard from "@/components/BuyerNotificationPrefCard";
import BuyerMatchedLeadsPanel from "@/components/BuyerMatchedLeadsPanel";

const AVATAR_GRADIENTS = [
  ["#10B981", "#059669"], ["#0EA5E9", "#0284C7"], ["#F59E0B", "#D97706"],
  ["#EC4899", "#DB2777"], ["#8B5CF6", "#7C3AED"], ["#EF4444", "#DC2626"],
  ["#14B8A6", "#0D9488"], ["#F97316", "#EA580C"],
];
function gradientForName(name) {
  const ch = (name || "?").trim().toUpperCase().charCodeAt(0) || 63;
  return AVATAR_GRADIENTS[ch % AVATAR_GRADIENTS.length];
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function BuyerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/buyers/${id}`);
      setBuyer(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-center py-20 text-[var(--ink-2)]" data-testid="buyer-detail-loading">
        <Loader2 className="animate-spin mx-auto mb-2" /> Loading buyer profile…
      </div>
    );
  }
  if (notFound || !buyer) {
    return (
      <div className="card text-center py-16" data-testid="buyer-detail-not-found">
        <div className="font-display font-bold text-lg">Buyer not found</div>
        <p className="text-sm text-[var(--ink-2)] mt-2">
          They may have been deleted, or you may have followed an old link.
        </p>
        <Link to="/app/buyers" className="btn-outline mt-4 inline-flex" data-testid="buyer-detail-back">
          <ArrowLeft size={14} /> Back to Buyers
        </Link>
      </div>
    );
  }

  const [from, to] = gradientForName(buyer.name);
  const initial = (buyer.name || "?").trim().slice(0, 1).toUpperCase();
  const score = buyer.engagement_score || 0;
  const hot = score >= 60;
  const isCapture = buyer.kind === "capture";

  return (
    <div data-testid={`buyer-detail-${id}`}>
      {/* Back link */}
      <button
        type="button"
        onClick={() => nav(-1)}
        className="btn-ghost mb-4"
        data-testid="buyer-detail-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Hero card */}
      <div className="card mb-5 relative overflow-hidden" data-testid="buyer-detail-hero">
        <div className="flex items-start gap-5 flex-wrap">
          <div
            className="buyer-avatar"
            style={{
              background: `linear-gradient(135deg, ${from}, ${to})`,
              width: 72, height: 72, fontSize: 28, borderRadius: 16,
            }}
          >
            {initial}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-extrabold text-3xl tracking-tight leading-tight">
                {buyer.name}
              </h1>
              {hot && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-rose-600 text-white">
                  <Flame size={10} /> HOT · {score}
                </span>
              )}
              {buyer.consistent_responder && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
                  <Reply size={10} /> CONSISTENT RESPONDER
                </span>
              )}
              {isCapture && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-300">
                  <Sparkles size={10} /> CAPTURE · {buyer.status}
                </span>
              )}
            </div>
            {buyer.company && (
              <div className="text-sm text-[var(--ink-2)] mt-1 flex items-center gap-1">
                <Building2 size={12} /> {buyer.company}
              </div>
            )}
            {buyer.source_lead_address && (
              <div className="text-sm text-[var(--ink-3)] mt-1 flex items-center gap-1">
                <MapPin size={12} /> Captured from <strong className="ml-1">{buyer.source_lead_address}</strong>
              </div>
            )}
            <div className="text-xs text-[var(--ink-3)] mt-2">
              Last activity {timeAgo(buyer.last_activity_at)}
            </div>
          </div>
        </div>

        {/* Contact + action row */}
        <div className="grid sm:grid-cols-2 gap-2 mt-5 pt-5 border-t border-[var(--line)]">
          {buyer.phone && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--line)]" data-testid="buyer-detail-phone">
              <Phone size={14} className="text-emerald-600 shrink-0" />
              <a href={`tel:${buyer.phone}`} className="font-mono flex-1 hover:text-emerald-600 transition-colors">{buyer.phone}</a>
              <CallButton phone={buyer.phone} variant="full" label="Call" />
            </div>
          )}
          {buyer.email && (
            <a
              href={`mailto:${buyer.email}`}
              className="flex items-center gap-2 p-3 rounded-lg border border-[var(--line)] hover:border-sky-500 hover:text-sky-600 transition-colors truncate"
              data-testid="buyer-detail-email"
            >
              <Mail size={14} className="text-sky-600" />
              <span className="truncate">{buyer.email}</span>
            </a>
          )}
        </div>
      </div>

      {/* Engagement stat strip */}
      <div className="grid sm:grid-cols-4 gap-3 mb-5" data-testid="buyer-detail-stats">
        <StatTile icon={Eye} label="Photo views (30d)" value={buyer.views_30d || 0} accent="#0EA5E9" />
        <StatTile icon={TrendingUp} label="Deals matched" value={buyer.deals_matched_count || 0} accent="#10B981" />
        <StatTile icon={Reply} label="SMS replies (30d)" value={buyer.replies_30d || 0} accent="#F59E0B" />
        <StatTile icon={Flame} label="Engagement score" value={score} accent={hot ? "#EF4444" : "#94A3B8"} />
      </div>

      {/* Buy box */}
      {(buyer.buy_box?.min_price || buyer.buy_box?.max_price || buyer.buy_box?.bedrooms || buyer.buy_box?.property_types) && (
        <div className="card mb-5" data-testid="buyer-detail-buybox">
          <div className="label-eyebrow mb-2">Buy box</div>
          <div className="flex flex-wrap gap-2">
            {(buyer.buy_box?.min_price || buyer.buy_box?.max_price) && (
              <Chip icon={DollarSign}>
                {buyer.buy_box.min_price ? fmtMoney(buyer.buy_box.min_price) : "$0"}
                {" – "}
                {buyer.buy_box.max_price ? fmtMoney(buyer.buy_box.max_price) : "any"}
              </Chip>
            )}
            {buyer.buy_box?.bedrooms && <Chip icon={Bed}>{buyer.buy_box.bedrooms}+ bd</Chip>}
            {buyer.buy_box?.property_types && <Chip icon={Building2}>{buyer.buy_box.property_types}</Chip>}
            {buyer.markets && <Chip icon={MapPin}>{buyer.markets}</Chip>}
          </div>
        </div>
      )}

      {/* Community · Layer 3 — Named buy-boxes + active matched leads */}
      {!isCapture && (
        <>
          <BuyerNotificationPrefCard
            buyerId={id}
            initialPref={buyer.notification_pref}
          />
          <BuyerBuyBoxesPanel buyerId={id} buyerName={buyer.name} />
          <BuyerMatchedLeadsPanel buyerId={id} />
        </>
      )}

      {/* Matched deals */}
      {(buyer.matched_deals || []).length > 0 && (
        <div className="card mb-5" data-testid="buyer-detail-deals">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold text-lg">Matched deals</div>
              <div className="text-xs text-[var(--ink-3)]">Active GO leads ranked for this buyer&apos;s buy-box</div>
            </div>
            <span className="text-xs text-[var(--ink-3)]">{buyer.matched_deals.length}</span>
          </div>
          <div className="space-y-2">
            {buyer.matched_deals.map((d) => (
              <Link
                key={d.lead_id}
                to={`/app/leads/${d.lead_id}/report`}
                className="flex items-center gap-3 p-3 rounded-lg border border-[var(--line)] hover:border-[var(--ink)] hover:bg-[var(--bg)] transition-colors"
                data-testid={`buyer-detail-deal-${d.lead_id}`}
              >
                <MapPin size={14} className="text-[var(--ink-3)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{d.property_address}</div>
                  <div className="text-xs text-[var(--ink-3)]">
                    {d.city}{d.state ? `, ${d.state}` : ""}
                    {d.estimated_arv ? ` · ARV ${fmtMoney(d.estimated_arv)}` : ""}
                  </div>
                </div>
                {d.match_score != null && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    Match {d.match_score}
                  </span>
                )}
                <ExternalLink size={12} className="text-[var(--ink-3)] shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent property views */}
      {(buyer.recent_views || []).length > 0 && (
        <div className="card mb-5" data-testid="buyer-detail-views">
          <div className="font-display font-bold text-lg mb-3">Recent property views</div>
          <div className="space-y-1.5">
            {buyer.recent_views.slice(0, 10).map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <Eye size={12} className="text-[var(--ink-3)] shrink-0" />
                <span className="flex-1 truncate">
                  {v.lead ? `${v.lead.property_address}${v.lead.city ? ", " + v.lead.city : ""}` : "Property report"}
                </span>
                <span className="text-xs text-[var(--ink-3)]">{timeAgo(v.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes / message */}
      {buyer.notes && (
        <div className="card mb-5" data-testid="buyer-detail-notes">
          <div className="label-eyebrow mb-2">
            {isCapture ? "Capture message" : "Notes"}
          </div>
          <div className="flex items-start gap-2 text-sm text-[var(--ink-2)]">
            <MessageSquare size={13} className="mt-0.5 shrink-0 text-[var(--ink-3)]" />
            <div className="whitespace-pre-wrap leading-relaxed">{buyer.notes}</div>
          </div>
        </div>
      )}

      {/* Call recordings */}
      <div className="card mb-5" data-testid="buyer-detail-recordings">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Mic size={14} className="text-indigo-600" />
            <div className="font-display font-bold text-lg">Call recordings</div>
          </div>
          <span className="text-xs text-[var(--ink-3)]">Playback any past call with this buyer</span>
        </div>
        <CallRecordingsList buyerId={id} />
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="card buyers-stat" data-testid={`stat-${label.replace(/[^a-z]/gi, '-').toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: accent }}>
          <Icon size={18} />
        </div>
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="buyer-pill" style={{ background: "#10B98114", color: "#047857", borderColor: "#10B98144" }}>
      <Icon size={10} /> {children}
    </span>
  );
}
